import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import ContentContainer from "templates/content-container/contentContainer";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ReloadButton from "ui-components/reload-button/reloadButton";
import { DataTable } from "ui-components/data-table";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { PAGE_IDS } from "app/data/common/pageIds";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import ScanningService from "app/data/dock-scan/scanningService";
import { ScanEventType, ScanLog, ScanResultStatus } from "app/data/dock-scan/models";
import { UserUtils } from "app/data/user/userUtils";
import { userSelector } from "slices/user/userSlice";
import { useDockEmployees } from "./hooks/useDockScanQueries";
import { useDockUsersRefresh } from "./hooks/useDockUsersRefresh";
import {
  DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS,
  DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT,
  getDockScanLogsListColumns,
} from "./dockScanLogsListColumns";
import ScanLogDetailSidebar from "./components/ScanLogDetailSidebar";
import { DOCK_SELECT_MENU_PORTAL } from "./selectMenuPortal";
import "features/shipment-reconciliation/revenue-insights/accessorialRevenueInsights.scss";
import "./dockUsers.scss";

const RESULT_OPTIONS: XGSSelectOption[] = [
  { value: "", label: "All Results" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILURE", label: "Failed" },
];

const EVENT_OPTIONS: XGSSelectOption[] = [
  { value: "", label: "All Event Types" },
  { value: "ITEM_PICKED_FOR_LOADING", label: "Picked" },
  { value: "ITEM_LOADED", label: "Loaded" },
  { value: "WRONG_DOOR_SCAN", label: "Wrong Door" },
  { value: "LOCATION_RECOMMENDED", label: "Loc Recommended" },
  { value: "FREIGHT_DAMAGE_REPORTED", label: "Damage Reported" },
  { value: "VALIDATION_FAILED", label: "Validation X" },
  { value: "WORKFLOW_REJECTED", label: "Workflow Rejected" },
  { value: "DUPLICATE_SUPPRESSED", label: "Duplicate" },
  { value: "SCAN_EXCEPTION", label: "Scan Exception" },
];

const ALL_EMPLOYEES_OPTION: XGSSelectOption = { value: "", label: "All Employees" };

function terminalToOption(terminal: { id: number; name: string; shortName?: string }): XGSSelectOption {
  return {
    value: String(terminal.id),
    label: terminal.shortName ? `${terminal.shortName} - ${terminal.name}` : terminal.name,
  };
}

const PAGE_SIZE = 50;
const scanningService = ScanningService.getInstance();

const DockScanLogsList: React.FC = () => {
  const userState = useSelector(userSelector);
  const location = useLocation();
  const isXGSAdmin = !!UserUtils.isXGSAdministrator(userState.profile);
  const activeTerminal = userState.activeTerminal;

  const employeeIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("employeeId") || undefined,
    [location.search]
  );

  const terminalOptions = useMemo(() => {
    const centers = userState.profile?.serviceCenters || [];
    return centers.map(terminalToOption);
  }, [userState.profile?.serviceCenters]);

  const [terminalFilter, setTerminalFilter] = useState<XGSSelectOption | null>(() =>
    activeTerminal ? terminalToOption(activeTerminal) : null
  );
  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState<XGSSelectOption>(RESULT_OPTIONS[0]);
  const [eventFilter, setEventFilter] = useState<XGSSelectOption>(EVENT_OPTIONS[0]);
  const [employeeFilter, setEmployeeFilter] = useState<XGSSelectOption>(ALL_EMPLOYEES_OPTION);
  const [selectedLog, setSelectedLog] = useState<ScanLog | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hidden = new Set(DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT);
    return DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hidden.has(id) }),
      {} as Record<string, boolean>
    );
  });

  // Sync page terminal filter when header active terminal changes.
  useEffect(() => {
    if (!activeTerminal) {
      setTerminalFilter(null);
      return;
    }
    setTerminalFilter(terminalToOption(activeTerminal));
  }, [activeTerminal]);

  const terminalNumber = useMemo(() => {
    if (isXGSAdmin) {
      const fromFilter = terminalFilter?.value ? Number(terminalFilter.value) : undefined;
      return fromFilter != null && !Number.isNaN(fromFilter) ? fromFilter : undefined;
    }
    return activeTerminal?.id != null ? Number(activeTerminal.id) : undefined;
  }, [isXGSAdmin, terminalFilter?.value, activeTerminal?.id]);

  const terminalName = useMemo(() => {
    if (isXGSAdmin) {
      if (!terminalFilter?.value) return "All terminals";
      const center = userState.profile?.serviceCenters?.find(
        (t) => String(t.id) === terminalFilter.value
      );
      return center?.name || terminalFilter.label;
    }
    return activeTerminal?.name || "Terminal";
  }, [
    isXGSAdmin,
    terminalFilter?.label,
    terminalFilter?.value,
    userState.profile?.serviceCenters,
    activeTerminal?.name,
  ]);

  const {
    refreshAll,
    isRefreshing,
    lastRefreshedLabel,
    setLastRefreshedAt,
  } = useDockUsersRefresh(terminalNumber);

  const {
    data: employeesData,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasMoreEmployees,
    isFetchingNextPage: isFetchingMoreEmployees,
  } = useDockEmployees(terminalNumber, "ACTIVE");

  useEffect(() => {
    if (hasMoreEmployees && !isFetchingMoreEmployees) {
      void fetchNextEmployeesPage();
    }
  }, [hasMoreEmployees, isFetchingMoreEmployees, fetchNextEmployeesPage]);

  const employeeOptions = useMemo(() => {
    const loaded =
      employeesData?.pages.flatMap((page) =>
        Array.isArray(page) ? page : page?.items ?? []
      ).filter(Boolean) ?? [];
    const opts: XGSSelectOption[] = [
      ALL_EMPLOYEES_OPTION,
      ...loaded.map((emp) => ({
        value: emp.employeeId,
        label: `${emp.firstName} ${emp.lastName} (${emp.employeeId})`.trim(),
      })),
    ];
    return opts;
  }, [employeesData]);

  useEffect(() => {
    setEmployeeFilter((prev) => {
      // Apply deep-link when unset, or refresh label while still on that employee.
      if (employeeIdFromUrl && (prev.value === "" || prev.value === employeeIdFromUrl)) {
        const match = employeeOptions.find((opt) => opt.value === employeeIdFromUrl);
        return match || { value: employeeIdFromUrl, label: `Employee ${employeeIdFromUrl}` };
      }
      if (!prev.value) return prev;
      const match = employeeOptions.find((opt) => opt.value === prev.value);
      return match && match.label !== prev.label ? match : prev;
    });
  }, [employeeIdFromUrl, employeeOptions]);

  // Reset employee filter when terminal changes (except URL deep-link for same employee).
  useEffect(() => {
    if (employeeIdFromUrl) return;
    setEmployeeFilter(ALL_EMPLOYEES_OPTION);
  }, [terminalNumber, employeeIdFromUrl]);

  const selectedEmployeeId = employeeFilter.value || undefined;

  const queryParams = useMemo(
    () => ({
      terminalId: terminalNumber != null ? String(terminalNumber) : undefined,
      take: PAGE_SIZE,
      employeeId: selectedEmployeeId,
      resultStatus: (resultFilter.value || undefined) as ScanResultStatus | undefined,
      eventType: (eventFilter.value || undefined) as ScanEventType | undefined,
    }),
    [terminalNumber, selectedEmployeeId, resultFilter.value, eventFilter.value]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.DOCK_SCAN_LOGS, "list", queryParams],
    queryFn: async ({ pageParam }) =>
      scanningService.listScanLogs({
        ...queryParams,
        cursor: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isXGSAdmin || terminalNumber != null,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if ((isXGSAdmin || terminalNumber != null) && data) {
      setLastRefreshedAt(Date.now());
    }
  }, [isXGSAdmin, terminalNumber, data, setLastRefreshedAt]);

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    [data]
  );

  const filteredItems = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (row) =>
        row.equipmentId?.toLowerCase().includes(q) ||
        row.barcodeValue?.toLowerCase().includes(q) ||
        row.resultCode?.toLowerCase().includes(q) ||
        String(row.probillNumber ?? "").toLowerCase().includes(q) ||
        String(row.rollNumber ?? "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const columns = useMemo(() => getDockScanLogsListColumns(), []);

  if (!isXGSAdmin && !terminalNumber) {
    return (
      <ContentContainer
        className="xgs-list xgs-list--full-width"
        mods={{ "full-width": true }}
        titleComponent={
          <ContentContainerToolbar
            pageId={PAGE_IDS.DOCK_SCAN_LOGS}
            title="Scan Logs"
            mods={{ "full-width": true }}
          />
        }
      >
        <div className="dock-users__empty">Select a terminal to view scan logs.</div>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.DOCK_SCAN_LOGS}
          title={`Scan Logs — ${terminalName}`}
          mods={{ "full-width": true }}
        >
          <div className="dock-users__refresh-toolbar">
            <span className="dock-users__last-refreshed">
              Last refreshed {lastRefreshedLabel ?? "—"}
            </span>
            <ReloadButton
              title="Refresh"
              spinner={isRefreshing || (isFetching && !isFetchingNextPage)}
              disabled={isRefreshing || (isFetching && !isFetchingNextPage)}
              onClick={() => void refreshAll()}
            />
          </div>
        </ContentContainerToolbar>
      }
    >
      <div className="dock-users__toolbar">
        <div className="dock-users__filters">
          <div className="dock-users__filter-field dock-users__filter-field--search">
            <LabeledTextInput
              labelMode={LabelModes.column}
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Barcode, equipment, probill, roll..."
            />
          </div>
          {isXGSAdmin && (
            <div className="dock-users__filter-field dock-users__filter-field--select">
              <LabeledSelectInput
                labelMode={LabelModes.column}
                label="Terminal"
                options={terminalOptions}
                value={terminalFilter || undefined}
                onValueChange={(opt) => setTerminalFilter(opt || null)}
                isSearchable
                clearable
                {...DOCK_SELECT_MENU_PORTAL}
              />
            </div>
          )}
          <div className="dock-users__filter-field dock-users__filter-field--select">
            <LabeledSelectInput
              labelMode={LabelModes.column}
              label="Employee"
              options={employeeOptions}
              value={employeeFilter}
              onValueChange={(opt) => setEmployeeFilter(opt || ALL_EMPLOYEES_OPTION)}
              isSearchable
              clearable
              {...DOCK_SELECT_MENU_PORTAL}
            />
          </div>
          <div className="dock-users__filter-field dock-users__filter-field--select">
            <LabeledSelectInput
              labelMode={LabelModes.column}
              label="Result"
              options={RESULT_OPTIONS}
              value={resultFilter}
              onValueChange={(opt) => setResultFilter(opt || RESULT_OPTIONS[0])}
              {...DOCK_SELECT_MENU_PORTAL}
            />
          </div>
          <div className="dock-users__filter-field dock-users__filter-field--select">
            <LabeledSelectInput
              labelMode={LabelModes.column}
              label="Event Type"
              options={EVENT_OPTIONS}
              value={eventFilter}
              onValueChange={(opt) => setEventFilter(opt || EVENT_OPTIONS[0])}
              {...DOCK_SELECT_MENU_PORTAL}
            />
          </div>
        </div>
        <span className="dock-users__record-count">
          {search.trim()
            ? `${filteredItems.length} of ${rows.length} loaded`
            : `Showing ${rows.length}`}
          {" · newest first"}
          {hasNextPage ? " · more available" : ""}
          {selectedEmployeeId ? ` · employee ${selectedEmployeeId}` : ""}
        </span>
      </div>

      {isError && (
        <XGSErrorMessage>{(error as Error)?.message || "Unable to load scan logs."}</XGSErrorMessage>
      )}
      {isLoading && <Loading isLoading />}

      {!isLoading && !isError && (
        <DataTable
          columns={columns}
          data={filteredItems}
          isLoading={isFetching && !isFetchingNextPage}
          rowHeight={56}
          responsive
          stickyHeaders
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnSelectorOptions={DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS}
          showColumnSelector
          cursorPointer
          onRowClicked={(original) => setSelectedLog(original as ScanLog)}
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={!!hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
        />
      )}

      <ScanLogDetailSidebar
        show={!!selectedLog}
        scanLog={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </ContentContainer>
  );
};

export default DockScanLogsList;
